// ============= MASTER EVENT DATA =============
// This is the SINGLE source of truth for ALL events in the application
// Every page and component must use this data to ensure consistency

interface BaseEvent {
  id: number;
  title: string;
  sport: string;
  date: string;
  endDate?: string;
  time: string;
  location: string;
  participants: number;
  maxParticipants: number;
  registrationFee: number;
  categories: string[];
  organizer: string;
  genderCategories: string[];
  subcategories: Record<string, string[]>;
  matchType: 'singles' | 'dobles' | 'ambos';
  registrationDeadline?: string;
  distance?: number;
  prizes?: CategoryPrizes[];
  registeredUsers?: number[]; // Array of user IDs who registered
}

interface Tournament extends BaseEvent {
  isLeague?: false;
  tournamentFormat: 'round-robin' | 'eliminacion-directa' | 'sumatoria-juegos';
}

interface League extends BaseEvent {
  isLeague: true;
  leagueFormat: 'round-robin' | 'sumatoria-juegos' | 'escalera-piramidal';
  duration: string;
  matchesPerWeek: number | string;
}

export type MasterEvent = Tournament | League;

export interface CategoryPrizes {
  category: string;
  subcategory: string;
  firstPlace: Prize[];
  secondPlace?: Prize[];
  thirdPlace?: Prize[];
}

export interface Prize {
  type: 'trofeo' | 'dinero' | 'pala' | 'raqueta' | 'certificado' | 'otro';
  amount?: number;
  description?: string;
}

// Current user ID for testing - in a real app this would come from auth
export const CURRENT_USER_ID = 1;

// ============= CONSISTENT DATA GENERATION =============
// These functions generate the same data based on event ID to ensure consistency
// between "Mis Eventos" cards and detail pages

interface ConsistentEventData {
  nextMatchOpponent: string;
  nextMatchPartner?: string;
  nextMatchCourt: string;
  nextMatchTime: string;
  currentStage: string;
  partnerName?: string;
}

// Predefined arrays to ensure consistency
const RIVALS_BY_SPORT = {
  'Pádel': [
    'Carlos Rivera / Laura Sánchez',
    'Diego López / Carmen Ruiz', 
    'Roberto Silva / Elena Torres',
    'Fernando Mora / Isabel Vega',
    'Miguel Castro / Sofía Herrera',
    'Juan Pérez / Ana Martín',
    'Andrés Jiménez / Lucía Mendoza',
    'Alberto Ruiz / Carmen Silva'
  ],
  'Tenis': [
    'Carlos Martínez',
    'Miguel Torres',
    'Roberto Díaz',
    'Javier Moreno',
    'Alberto Ruiz',
    'David López',
    'Fernando López',
    'Marcos Torres'
  ],
  'Pickleball': [
    'Ana López / Pedro Sánchez',
    'Andrea Vega / Luis Fernández',
    'Carmen Morales / Diego Fernández',
    'Laura Sánchez / Roberto Jiménez',
    'Elena Vega / Carlos Martínez',
    'María López / Luis Fernández',
    'Sandra Ruiz / Diego Fernández',
    'Ana Rodríguez / Roberto Jiménez'
  ]
};

const PARTNERS_BY_SPORT = {
  'Pádel': ['María González', 'Elena Rodríguez', 'Carmen Silva', 'Laura Martín'],
  'Tenis': ['Ana López', 'Carmen Morales', 'Laura Sánchez', 'Elena Vega'],
  'Pickleball': ['Sandra Ruiz', 'Ana Rodríguez', 'Carmen Vega', 'María Torres']
};

const COURTS = ['Central', '1', '2', '3', '4'];
const TIMES = ['10:00', '11:00', '14:00', '16:00', '18:00', '19:00', '20:00'];

const TOURNAMENT_STAGES = [
  'Fase de Grupos - Grupo A',
  'Octavos de Final', 
  'Cuartos de Final',
  'Ronda 3 de 5',
  'Ronda 2 de 4'
];

const LEAGUE_STAGES = [
  'Jornada 8 de 15',
  'Temporada Regular - 145 juegos acumulados',
  'Posición 8 de 20', 
  'Jornada 5 de 12',
  'Semana 4 de 8'
];

// Generate consistent data based on event ID
export function generateConsistentEventData(eventId: number, sport: string, matchType: 'singles' | 'dobles' | 'ambos', isLeague: boolean): ConsistentEventData {
  // Use event ID as seed for consistent selection
  const sportRivals = RIVALS_BY_SPORT[sport] || RIVALS_BY_SPORT['Pádel'];
  const sportPartners = PARTNERS_BY_SPORT[sport] || PARTNERS_BY_SPORT['Pádel'];
  
  // Generate consistent indices based on event ID
  const rivalIndex = eventId % sportRivals.length;
  const partnerIndex = eventId % sportPartners.length;
  const courtIndex = eventId % COURTS.length;
  const timeIndex = eventId % TIMES.length;
  const stageIndex = eventId % (isLeague ? LEAGUE_STAGES.length : TOURNAMENT_STAGES.length);
  
  // Get next match opponent
  let nextMatchOpponent = sportRivals[rivalIndex];
  
  // For singles, extract just the first name
  if (matchType === 'singles') {
    nextMatchOpponent = nextMatchOpponent.split(' / ')[0] || nextMatchOpponent.split(' ')[0];
  }
  
  // Get partner for doubles
  let partnerName: string | undefined;
  let nextMatchPartner: string | undefined;
  
  if (matchType === 'dobles' || matchType === 'ambos') {
    partnerName = sportPartners[partnerIndex];
    nextMatchPartner = partnerName;
  }
  
  return {
    nextMatchOpponent,
    nextMatchPartner,
    nextMatchCourt: COURTS[courtIndex],
    nextMatchTime: TIMES[timeIndex],
    currentStage: isLeague ? LEAGUE_STAGES[stageIndex] : TOURNAMENT_STAGES[stageIndex],
    partnerName
  };
}

// ============= ALL EVENTS IN THE SYSTEM =============
export const MASTER_EVENTS: MasterEvent[] = [
  // === ACTIVE TOURNAMENTS (USUARIO INSCRITO) - 6 formats (3 base + 3 additional) ===
  // 1. Round Robin Tournament - Pádel Dobles
  {
    id: 1,
    title: "Torneo Pádel Round Robin - Copa Activa",
    sport: "Pádel",
    date: "2025-09-09", // Active (started 2 days ago)
    endDate: "2025-09-20",
    time: "10:00",
    location: "Centro Pádel Madrid",
    participants: 16,
    maxParticipants: 20,
    registrationFee: 40,
    categories: ["2", "3"],
    distance: 7.5,
    organizer: "Centro Pádel Madrid",
    genderCategories: ["Mixtos"],
    subcategories: {
      "Mixtos": ["A", "B"]
    },
    matchType: "dobles",
    tournamentFormat: "round-robin",
    registrationDeadline: "2025-07-27",
    registeredUsers: [1],
    prizes: [
      {
        category: "Mixtos",
        subcategory: "A",
        firstPlace: [
          { type: "trofeo" },
          { type: "dinero", amount: 180 }
        ],
        secondPlace: [
          { type: "dinero", amount: 90 },
          { type: "pala" }
        ]
      }
    ]
  },
  // 2. Elimination Tournament - Tenis Singles
  {
    id: 2,
    title: "Torneo Tenis Eliminación Directa",
    sport: "Tenis",
    date: "2025-09-08", // Active (started 3 days ago)
    endDate: "2025-09-18",
    time: "16:00",
    location: "Tennis Club Elite",
    participants: 8,
    maxParticipants: 16,
    registrationFee: 30,
    categories: ["2"],
    distance: 8.2,
    organizer: "Tennis Club Elite",
    genderCategories: ["Masculina", "Femenina"],
    subcategories: {
      "Masculina": ["A", "B"],
      "Femenina": ["B", "C"]
    },
    matchType: "singles",
    tournamentFormat: "eliminacion-directa",
    registrationDeadline: "2025-07-28",
    registeredUsers: [1],
    prizes: [
      {
        category: "Masculina",
        subcategory: "A",
        firstPlace: [
          { type: "trofeo" },
          { type: "raqueta" }
        ],
        secondPlace: [
          { type: "dinero", amount: 100 }
        ]
      }
    ]
  },
  // 3. Sum of Games Tournament - Pickleball Singles
  {
    id: 3,
    title: "Torneo Pickleball Sumatoria de Juegos",
    sport: "Pickleball",
    date: "2025-09-10", // Active (started yesterday)
    endDate: "2025-09-22",
    time: "14:00",
    location: "Polideportivo Centro",
    participants: 12,
    maxParticipants: 16,
    registrationFee: 30,
    categories: ["3", "4"],
    distance: 5.2,
    organizer: "Club Pickleball Centro",
    genderCategories: ["Masculina", "Femenina"],
    subcategories: {
      "Masculina": ["A"],
      "Femenina": ["A"]
    },
    matchType: "singles",
    tournamentFormat: "sumatoria-juegos",
    registrationDeadline: "2025-07-25",
    registeredUsers: [1],
    prizes: [
      {
        category: "Masculina",
        subcategory: "A",
        firstPlace: [
          { type: "trofeo" },
          { type: "otro", description: "Kit Pickleball Profesional" }
        ],
        secondPlace: [
          { type: "dinero", amount: 75 }
        ]
      }
    ]
  },
  
  // === ACTIVE LEAGUES (USUARIO INSCRITO) - all 3 league formats ===
  // 1. Round Robin League - Pádel Dobles (ACTIVE)
  {
    id: 10,
    title: "Liga Pádel Round Robin - Verano Activa",
    sport: "Pádel",
    date: "2025-09-07", // Active (started 4 days ago)
    endDate: "2025-09-30",
    time: "09:00",
    location: "Club Deportivo Central",
    participants: 24,
    maxParticipants: 32,
    registrationFee: 45,
    categories: ["1", "2", "3"],
    duration: "8 semanas",
    matchesPerWeek: 2,
    organizer: "Club Deportivo Central",
    genderCategories: ["Mixtos"],
    subcategories: {
      "Mixtos": ["A", "B", "C", "D"]
    },
    matchType: "dobles",
    isLeague: true,
    registrationDeadline: "2025-07-28",
    leagueFormat: "round-robin",
    registeredUsers: [1],
    prizes: [
      {
        category: "Mixtos",
        subcategory: "A",
        firstPlace: [
          { type: "trofeo" },
          { type: "dinero", amount: 250 }
        ]
      }
    ]
  },
  // 2. Sum of Games League - Tenis Singles (ACTIVE)
  {
    id: 11,
    title: "Liga Tenis Sumatoria de Juegos",
    sport: "Tenis",
    date: "2025-09-08", // Active (started 3 days ago) 
    endDate: "2025-09-25",
    time: "18:00",
    location: "Tennis Club Elite",
    participants: 16,
    maxParticipants: 24,
    registrationFee: 75,
    categories: ["2", "3"],
    duration: "8 semanas",
    matchesPerWeek: 1,
    organizer: "Tennis Club Elite",
    genderCategories: ["Masculina", "Femenina"],
    subcategories: {
      "Masculina": ["A", "B"],
      "Femenina": ["B", "C"]
    },
    matchType: "singles",
    isLeague: true,
    registrationDeadline: "2025-07-25",
    leagueFormat: "sumatoria-juegos",
    registeredUsers: [1],
    distance: 8.2,
    prizes: [
      {
        category: "Masculina",
        subcategory: "A",
        firstPlace: [
          { type: "trofeo" },
          { type: "raqueta" }
        ]
      }
    ]
  },
  // 3. Ladder League - Pádel Singles (ACTIVE)
  {
    id: 18,
    title: "Liga Escalera Pádel Singles",
    sport: "Pádel",
    date: "2025-09-09", // Active (started 2 days ago)
    endDate: "2025-09-27",
    time: "10:00",
    location: "Club Deportivo Central",
    participants: 8,
    maxParticipants: 8,
    registrationFee: 350,
    categories: ["2"],
    isLeague: true,
    leagueFormat: "escalera-piramidal",
    duration: "8 semanas",
    matchesPerWeek: 3,
    organizer: "Club Deportivo Central",
    genderCategories: ["Masculina"],
    subcategories: {
      "Masculina": ["2nda"]
    },
    matchType: "singles",
    registrationDeadline: "2025-07-27",
    registeredUsers: [1],
    prizes: [
      {
        category: "Masculina",
        subcategory: "2nda",
        firstPlace: [
          { type: "trofeo" },
          { type: "otro", description: "Paleta de Pádel Profesional" },
          { type: "dinero", amount: 400 }
        ]
      }
    ]
  },
  // 4. Ladder League - Pickleball Dobles (ACTIVE)
  {
    id: 19,
    title: "Liga Escalera Pickleball Dobles",
    sport: "Pickleball",
    date: "2025-07-23", // Active (started yesterday)
    endDate: "2025-09-20",
    time: "19:30",
    location: "Centro Pickleball Activo",
    participants: 14,
    maxParticipants: 18,
    registrationFee: 50,
    categories: ["2", "3"],
    duration: "10 semanas",
    matchesPerWeek: 1,
    organizer: "Club Pickleball Activo",
    genderCategories: ["Mixtos"],
    subcategories: {
      "Mixtos": ["A", "B"]
    },
    matchType: "dobles",
    isLeague: true,
    registrationDeadline: "2025-07-24",
    leagueFormat: "escalera-piramidal",
    registeredUsers: [1],
    distance: 12.5,
    prizes: [
      {
        category: "Mixtos",
        subcategory: "A",
        firstPlace: [
          { type: "trofeo" },
          { type: "otro", description: "Equipo Pickleball Premium" }
        ]
      }
    ]
  },

  // === ADDITIONAL ACTIVE TOURNAMENTS (USUARIO INSCRITO) ===
  // 4. Round Robin Tournament - Tenis Dobles (FINISHED)
  {
    id: 26,
    title: "Torneo Tenis Round Robin - Championship",
    sport: "Tenis",
    date: "2025-07-10", // Finished (2 weeks ago)
    endDate: "2025-07-14",
    time: "11:00",
    location: "Club Tenis Barcelona",
    participants: 20,
    maxParticipants: 24,
    registrationFee: 55,
    categories: ["1", "2"],
    distance: 12.8,
    organizer: "Club Tenis Barcelona",
    genderCategories: ["Mixtos"],
    subcategories: {
      "Mixtos": ["A", "B", "C"]
    },
    matchType: "dobles",
    tournamentFormat: "round-robin",
    registrationDeadline: "2025-07-28",
    registeredUsers: [1],
    prizes: [
      {
        category: "Mixtos",
        subcategory: "A",
        firstPlace: [
          { type: "trofeo" },
          { type: "dinero", amount: 200 },
          { type: "raqueta" }
        ]
      }
    ]
  },
  // 5. Elimination Tournament - Pádel Dobles (FINISHED)
  {
    id: 27,
    title: "Torneo Pádel Eliminación - Pro Series",
    sport: "Pádel",
    date: "2025-07-05", // Finished (3 weeks ago)
    endDate: "2025-07-09",
    time: "09:30",
    location: "Pádel Center Valencia",
    participants: 16,
    maxParticipants: 24,
    registrationFee: 60,
    categories: ["1", "2"],
    distance: 9.5,
    organizer: "Pádel Center Valencia",
    genderCategories: ["Masculina", "Femenina"],
    subcategories: {
      "Masculina": ["A"],
      "Femenina": ["A"]
    },
    matchType: "dobles",
    tournamentFormat: "eliminacion-directa",
    registrationDeadline: "2025-07-08",
    registeredUsers: [1],
    prizes: [
      {
        category: "Masculina",
        subcategory: "A",
        firstPlace: [
          { type: "trofeo" },
          { type: "dinero", amount: 400 },
          { type: "pala" }
        ]
      }
    ]
  },
  // 6. Sum of Games Tournament - Tenis Singles (FINISHED)
  {
    id: 28,
    title: "Torneo Tenis Sumatoria - Elite Open",
    sport: "Tenis",
    date: "2025-07-01", // Finished (3 weeks ago)
    endDate: "2025-07-03",
    time: "15:30",
    location: "Tennis Academy Pro",
    participants: 14,
    maxParticipants: 18,
    registrationFee: 45,
    categories: ["2", "3"],
    distance: 6.7,
    organizer: "Tennis Academy Pro",
    genderCategories: ["Masculina", "Femenina"],
    subcategories: {
      "Masculina": ["B"],
      "Femenina": ["B"]
    },
    matchType: "singles",
    tournamentFormat: "sumatoria-juegos",
    registrationDeadline: "2025-07-02",
    registeredUsers: [1],
    prizes: [
      {
        category: "Masculina",
        subcategory: "B",
        firstPlace: [
          { type: "trofeo" },
          { type: "dinero", amount: 150 }
        ]
      }
    ]
  },


  // === ADDITIONAL ACTIVE TOURNAMENTS (more formats) ===
  // 5. Round Robin Tournament - Pádel Dobles (ACTIVE)
  {
    id: 32,
    title: "Torneo Pádel Round Robin - Copa Verano",
    sport: "Pádel",
    date: "2025-07-24", // Active (starts today)
    endDate: "2025-07-28",
    time: "10:00",
    location: "Club Pádel Mediterráneo",
    participants: 16,
    maxParticipants: 20,
    registrationFee: 45,
    categories: ["2", "3"],
    distance: 6.8,
    organizer: "Club Pádel Mediterráneo",
    genderCategories: ["Mixtos"],
    subcategories: {
      "Mixtos": ["A", "B"]
    },
    matchType: "dobles",
    tournamentFormat: "round-robin",
    registrationDeadline: "2025-07-29",
    registeredUsers: [1],
    prizes: [
      {
        category: "Mixtos",
        subcategory: "A",
        firstPlace: [
          { type: "trofeo" },
          { type: "dinero", amount: 150 },
          { type: "pala" }
        ]
      }
    ]
  },
  // 6. Elimination Tournament - Pickleball Dobles (ACTIVE)
  {
    id: 33,
    title: "Torneo Pickleball Eliminación - Summer Championship",
    sport: "Pickleball",
    date: "2025-07-24", // Active (starts today)
    endDate: "2025-07-30",
    time: "16:00",
    location: "Pickleball Arena Sport",
    participants: 12,
    maxParticipants: 16,
    registrationFee: 35,
    categories: ["2", "3"],
    distance: 7.2,
    organizer: "Pickleball Arena Sport",
    genderCategories: ["Masculina", "Femenina"],
    subcategories: {
      "Masculina": ["A"],
      "Femenina": ["A"]
    },
    matchType: "dobles",
    tournamentFormat: "eliminacion-directa",
    registrationDeadline: "2025-07-30",
    registeredUsers: [1],
    prizes: [
      {
        category: "Masculina",
        subcategory: "A",
        firstPlace: [
          { type: "trofeo" },
          { type: "otro", description: "Set Pickleball Premium" }
        ]
      }
    ]
  },
  // 7. Sum Games Tournament - Tenis Dobles (ACTIVE)  
  {
    id: 34,
    title: "Torneo Tenis Sumatoria - Master Cup",
    sport: "Tenis",
    date: "2025-07-24", // Active (starts today)
    endDate: "2025-08-02",
    time: "18:30",
    location: "Tennis Club Premium",
    participants: 20,
    maxParticipants: 24,
    registrationFee: 65,
    categories: ["1", "2"],
    distance: 9.8,
    organizer: "Tennis Club Premium",
    genderCategories: ["Mixtos"],
    subcategories: {
      "Mixtos": ["A", "B"]
    },
    matchType: "dobles",
    tournamentFormat: "sumatoria-juegos",
    registrationDeadline: "2025-08-01",
    registeredUsers: [1],
    prizes: [
      {
        category: "Mixtos",
        subcategory: "A",
        firstPlace: [
          { type: "trofeo" },
          { type: "dinero", amount: 300 },
          { type: "raqueta" }
        ]
      }
    ]
  },

  // === FINISHED EVENTS (USUARIO INSCRITO) ===
  // Finished League - Round Robin (ID 29)
  {
    id: 29,
    title: "Liga Escalera Pickleball - Advanced",
    sport: "Pickleball", 
    date: "2025-06-01", // Finished (7 weeks ago)
    endDate: "2025-06-30",
    time: "17:00",
    location: "Centro Pickleball Pro",
    participants: 12,
    maxParticipants: 12,
    registrationFee: 60,
    categories: ["2", "3"],
    duration: "4 semanas",
    matchesPerWeek: 2,
    organizer: "Centro Pickleball Pro",
    genderCategories: ["Mixtos"],
    subcategories: {
      "Mixtos": ["A", "B"]
    },
    matchType: "dobles",
    isLeague: true,
    registrationDeadline: "2025-05-30",
    leagueFormat: "escalera-piramidal",
    registeredUsers: [1],
    distance: 8.5,
    prizes: [
      {
        category: "Mixtos",
        subcategory: "A",
        firstPlace: [
          { type: "trofeo" },
          { type: "dinero", amount: 150 }
        ]
      }
    ]
  },
  
  // Additional finished league - Round Robin (ID 30) 
  {
    id: 30,
    title: "Liga Pádel Round Robin - Copa Invierno (Finalizada)",
    sport: "Pádel",
    date: "2025-05-15", // Finished (10 weeks ago)
    endDate: "2025-06-15",
    time: "18:00",
    location: "Club Pádel Central", 
    participants: 20,
    maxParticipants: 20,
    registrationFee: 55,
    categories: ["2", "3"],
    duration: "5 semanas",
    matchesPerWeek: 2,
    organizer: "Club Pádel Central",
    genderCategories: ["Mixtos"],
    subcategories: {
      "Mixtos": ["A", "B", "C"]
    },
    matchType: "dobles",
    isLeague: true,
    registrationDeadline: "2025-05-12",
    leagueFormat: "round-robin",
    registeredUsers: [1],
    distance: 6.2,
    prizes: [
      {
        category: "Mixtos",
        subcategory: "A",
        firstPlace: [
          { type: "trofeo" },
          { type: "dinero", amount: 200 }
        ]
      }
    ]
  },
  
  // Additional finished league - Sum of Games (ID 31)
  {
    id: 31, 
    title: "Liga Tenis Sumatoria - Copa Primavera (Finalizada)",
    sport: "Tenis",
    date: "2025-04-01", // Finished (15 weeks ago)
    endDate: "2025-04-30",
    time: "16:00",
    location: "Tennis Club Elite",
    participants: 16,
    maxParticipants: 16,
    registrationFee: 70,
    categories: ["2", "3"],
    duration: "4 semanas",
    matchesPerWeek: 1,
    organizer: "Tennis Club Elite",
    genderCategories: ["Masculina", "Femenina"],
    subcategories: {
      "Masculina": ["A"],
      "Femenina": ["A"]
    },
    matchType: "singles",
    isLeague: true,
    registrationDeadline: "2025-03-28",
    leagueFormat: "sumatoria-juegos",
    registeredUsers: [1],
    distance: 9.1,
    prizes: [
      {
        category: "Masculina",
        subcategory: "A",
        firstPlace: [
          { type: "trofeo" },
          { type: "raqueta" }
        ]
      }
    ]
  },

  {
    id: 4,
    title: "Copa de Tenis Veteranos - Finalizada",
    sport: "Tenis",
    date: "2025-06-20", // Finished
    endDate: "2025-06-22",
    time: "10:00",
    location: "Club Raqueta de Oro",
    participants: 16,
    maxParticipants: 32,
    registrationFee: 50,
    categories: ["4", "open"],
    distance: 4.1,
    organizer: "Club Raqueta de Oro",
    genderCategories: ["Masculina"],
    subcategories: {
      "Masculina": ["A", "B", "C"]
    },
    matchType: "dobles",
    tournamentFormat: "eliminacion-directa",
    registrationDeadline: "2025-06-18",
    registeredUsers: [1],
    prizes: [
      {
        category: "Masculina",
        subcategory: "A",
        firstPlace: [
          { type: "trofeo" },
          { type: "dinero", amount: 300 }
        ]
      }
    ]
  },
  {
    id: 12,
    title: "Liga Pickleball Recreativa - Finalizada",
    sport: "Pickleball",
    date: "2025-06-15", // Finished
    endDate: "2025-07-01",
    time: "19:00",
    location: "Centro Deportivo Norte",
    participants: 12,
    maxParticipants: 20,
    registrationFee: 55,
    categories: ["3", "4"],
    duration: "6 semanas",
    matchesPerWeek: 1,
    organizer: "Centro Deportivo Norte",
    genderCategories: ["Masculina", "Femenina", "Mixtos"],
    subcategories: {
      "Masculina": ["B", "C"],
      "Femenina": ["C", "D"],
      "Mixtos": ["B", "C", "D"]
    },
    matchType: "ambos",
    isLeague: true,
    registrationDeadline: "2025-06-13",
    leagueFormat: "escalera-piramidal",
    registeredUsers: [1],
    prizes: [
      {
        category: "Mixtos",
        subcategory: "B",
        firstPlace: [
          { type: "trofeo" },
          { type: "otro", description: "Kit de equipamiento Pickleball" }
        ]
      }
    ]
  },

  // === UPCOMING EVENTS (USUARIO INSCRITO) ===
  {
    id: 5,
    title: "Torneo Pickleball Abierto - Próximo",
    sport: "Pickleball",
    date: "2025-07-26", // Upcoming (tomorrow)
    endDate: "2025-07-28",
    time: "14:00",
    location: "Centro Deportivo Norte",
    participants: 12,
    maxParticipants: 20,
    registrationFee: 25,
    categories: ["1", "2", "3", "4", "5"],
    distance: 15.3,
    organizer: "Centro Deportivo Norte",
    genderCategories: ["Mixtos"],
    subcategories: {
      "Mixtos": ["A", "B", "C", "D"]
    },
    matchType: "ambos",
    tournamentFormat: "round-robin",
    registrationDeadline: "2025-07-27",
    registeredUsers: [1],
    prizes: [
      {
        category: "Mixtos",
        subcategory: "A",
        firstPlace: [
          { type: "trofeo" },
          { type: "otro", description: "Set de pelotas Pickleball" }
        ]
      }
    ]
  },

  // === AVAILABLE EVENTS (NOT REGISTERED) ===
  {
    id: 6,
    title: "Torneo de Pádel Elite - Eliminación Directa",
    sport: "Pádel",
    date: "2025-07-27", // Upcoming (in 3 days)
    endDate: "2025-07-29",
    time: "09:00",
    location: "Centro Pádel Elite Sevilla",
    participants: 16,
    maxParticipants: 32,
    registrationFee: 45,
    categories: ["A"],
    distance: 18.5,
    organizer: "Club Elite Sevilla",
    genderCategories: ["Mixtos"],
    subcategories: {
      "Mixtos": ["A"]
    },
    matchType: "dobles",
    tournamentFormat: "eliminacion-directa",
    registrationDeadline: "2025-08-02",
    registeredUsers: [],
    prizes: [
      {
        category: "Mixtos",
        subcategory: "A",
        firstPlace: [
          { type: "trofeo" },
          { type: "dinero", amount: 300 }
        ]
      }
    ]
  },
  {
    id: 7,
    title: "Liga Pádel Profesional - División Elite",
    sport: "Pádel",
    date: "2025-07-29", // Upcoming (in 5 days)
    endDate: "2025-10-15",
    time: "20:00",
    location: "Centro Pádel Premium",
    participants: 18,
    maxParticipants: 24,
    registrationFee: 85,
    categories: ["1", "A"],
    duration: "10 semanas",
    matchesPerWeek: 2,
    organizer: "Liga Profesional Pádel",
    genderCategories: ["Masculina", "Femenina"],
    subcategories: {
      "Masculina": ["1era"],
      "Femenina": ["1era"]
    },
    matchType: "dobles",
    isLeague: true,
    registrationDeadline: "2025-08-07",
    leagueFormat: "round-robin",
    distance: 14.3,
    registeredUsers: [],
    prizes: [
      {
        category: "Masculina",
        subcategory: "1era",
        firstPlace: [
          { type: "trofeo" },
          { type: "dinero", amount: 500 }
        ]
      }
    ]
  },

  // === UPCOMING TOURNAMENTS (USUARIO INSCRITO) - 3 formats ===
  // 1. Round Robin Tournament - Pádel Dobles (UPCOMING)
  {
    id: 20,
    title: "Torneo Pádel Round Robin - Copa Primavera",
    sport: "Pádel",
    date: "2025-08-15", // Upcoming
    endDate: "2025-08-18",
    time: "09:00",
    location: "Club Pádel Valencia",
    participants: 0,
    maxParticipants: 24,
    registrationFee: 45,
    categories: ["1", "2"],
    distance: 12.3,
    organizer: "Club Pádel Valencia",
    genderCategories: ["Mixtos"],
    subcategories: {
      "Mixtos": ["A", "B"]
    },
    matchType: "dobles",
    tournamentFormat: "round-robin",
    registrationDeadline: "2025-08-12",
    registeredUsers: [1],
    prizes: [
      {
        category: "Mixtos",
        subcategory: "A",
        firstPlace: [
          { type: "trofeo" },
          { type: "dinero", amount: 200 }
        ]
      }
    ]
  },
  // 2. Elimination Tournament - Tenis Singles (UPCOMING)
  {
    id: 21,
    title: "Torneo Tenis Eliminación - Championship",
    sport: "Tenis",
    date: "2025-08-20", // Upcoming
    endDate: "2025-08-25",
    time: "15:00",
    location: "Tennis Club Barcelona",
    participants: 0,
    maxParticipants: 32,
    registrationFee: 50,
    categories: ["1", "2"],
    distance: 15.7,
    organizer: "Tennis Club Barcelona",
    genderCategories: ["Masculina", "Femenina"],
    subcategories: {
      "Masculina": ["A", "B"],
      "Femenina": ["A", "B"]
    },
    matchType: "singles",
    tournamentFormat: "eliminacion-directa",
    registrationDeadline: "2025-08-17",
    registeredUsers: [1],
    prizes: [
      {
        category: "Masculina",
        subcategory: "A",
        firstPlace: [
          { type: "trofeo" },
          { type: "raqueta" },
          { type: "dinero", amount: 300 }
        ]
      }
    ]
  },
  // 3. Sum of Games Tournament - Pickleball Dobles (UPCOMING)
  {
    id: 22,
    title: "Torneo Pickleball Sumatoria - Open",
    sport: "Pickleball",
    date: "2025-08-25", // Upcoming
    endDate: "2025-08-28",
    time: "10:00",
    location: "Centro Pickleball Sevilla",
    participants: 0,
    maxParticipants: 20,
    registrationFee: 35,
    categories: ["2", "3"],
    distance: 8.9,
    organizer: "Centro Pickleball Sevilla",
    genderCategories: ["Mixtos"],
    subcategories: {
      "Mixtos": ["A", "B"]
    },
    matchType: "dobles",
    tournamentFormat: "sumatoria-juegos",
    registrationDeadline: "2025-08-22",
    registeredUsers: [1],
    prizes: [
      {
        category: "Mixtos",
        subcategory: "A",
        firstPlace: [
          { type: "trofeo" },
          { type: "otro", description: "Set Completo Pickleball Pro" }
        ]
      }
    ]
  },

  // === UPCOMING LEAGUES (USUARIO INSCRITO) - 3 formats ===
  // 1. Round Robin League - Tenis Dobles (UPCOMING)
  {
    id: 23,
    title: "Liga Tenis Round Robin - Copa Otoño",
    sport: "Tenis",
    date: "2025-09-01", // Upcoming
    endDate: "2025-11-15",
    time: "18:00",
    location: "Tennis Club Madrid",
    participants: 0,
    maxParticipants: 28,
    registrationFee: 80,
    categories: ["1", "2"],
    duration: "10 semanas",
    matchesPerWeek: 2,
    organizer: "Tennis Club Madrid",
    genderCategories: ["Mixtos"],
    subcategories: {
      "Mixtos": ["A", "B", "C"]
    },
    matchType: "dobles",
    isLeague: true,
    registrationDeadline: "2025-08-28",
    leagueFormat: "round-robin",
    registeredUsers: [1],
    distance: 6.4,
    prizes: [
      {
        category: "Mixtos",
        subcategory: "A",
        firstPlace: [
          { type: "trofeo" },
          { type: "dinero", amount: 400 }
        ]
      }
    ]
  },
  // 2. Sum of Games League - Pádel Singles (UPCOMING)
  {
    id: 24,
    title: "Liga Pádel Sumatoria - Elite",
    sport: "Pádel",
    date: "2025-09-05", // Upcoming
    endDate: "2025-11-20",
    time: "20:00",
    location: "Centro Pádel Elite",
    participants: 0,
    maxParticipants: 16,
    registrationFee: 90,
    categories: ["1"],
    duration: "11 semanas",
    matchesPerWeek: 1,
    organizer: "Centro Pádel Elite",
    genderCategories: ["Masculina", "Femenina"],
    subcategories: {
      "Masculina": ["1era"],
      "Femenina": ["1era"]
    },
    matchType: "singles",
    isLeague: true,
    registrationDeadline: "2025-09-02",
    leagueFormat: "sumatoria-juegos",
    registeredUsers: [1],
    distance: 11.2,
    prizes: [
      {
        category: "Masculina",
        subcategory: "1era",
        firstPlace: [
          { type: "trofeo" },
          { type: "dinero", amount: 500 },
          { type: "otro", description: "Paleta Profesional" }
        ]
      }
    ]
  },
  // 3. Ladder League - Pickleball Singles (UPCOMING)
  {
    id: 25,
    title: "Liga Escalera Pickleball - Champions",
    sport: "Pickleball",
    date: "2025-09-10", // Upcoming
    endDate: "2025-12-01",
    time: "17:00",
    location: "Pickleball Center Pro",
    participants: 0,
    maxParticipants: 12,
    registrationFee: 70,
    categories: ["1", "2"],
    duration: "12 semanas",
    matchesPerWeek: 2,
    organizer: "Pickleball Center Pro",
    genderCategories: ["Masculina", "Femenina"],
    subcategories: {
      "Masculina": ["A"],
      "Femenina": ["A"]
    },
    matchType: "singles",
    isLeague: true,
    registrationDeadline: "2025-09-07",
    leagueFormat: "escalera-piramidal",
    registeredUsers: [1],
    distance: 9.8,
    prizes: [
      {
        category: "Masculina",
        subcategory: "A",
        firstPlace: [
          { type: "trofeo" },
          { type: "dinero", amount: 350 }
        ]
      }
    ]
  },

  // === ADDITIONAL EVENTS FOR MOBILE TESTING ===
  // ACTIVE - Liga Pickleball Round Robin Singles
  {
    id: 35,
    title: "Liga Pickleball Round Robin Competitiva - Temporada Verano",
    sport: "Pickleball",
    date: "2025-09-01", // Active (started 9 days ago)
    endDate: "2025-09-30",
    time: "19:00",
    location: "Complejo Deportivo Pickleball Pro",
    participants: 16,
    maxParticipants: 20,
    registrationFee: 55,
    categories: ["2", "3"],
    duration: "7 semanas",
    matchesPerWeek: 2,
    organizer: "Pickleball Pro Academy",
    genderCategories: ["Masculina", "Femenina"],
    subcategories: {
      "Masculina": ["A", "B"],
      "Femenina": ["A", "B"]
    },
    matchType: "singles",
    isLeague: true,
    registrationDeadline: "2025-08-29",
    leagueFormat: "round-robin",
    registeredUsers: [1],
    distance: 7.2,
    prizes: [
      {
        category: "Masculina",
        subcategory: "A",
        firstPlace: [
          { type: "trofeo" },
          { type: "dinero", amount: 180 },
          { type: "otro", description: "Kit Pickleball Profesional" }
        ]
      }
    ]
  },

  // ACTIVE - Torneo Tenis Round Robin Dobles  
  {
    id: 36,
    title: "Copa Tenis Round Robin Dobles",
    sport: "Tenis",
    date: "2025-09-05", // Active (started 5 days ago)
    endDate: "2025-09-15",
    time: "16:30",
    location: "Tennis Club Metropolitan",
    participants: 12,
    maxParticipants: 16,
    registrationFee: 42,
    categories: ["2", "3"],
    distance: 11.5,
    organizer: "Tennis Club Metropolitan",
    genderCategories: ["Mixtos"],
    subcategories: {
      "Mixtos": ["A", "B"]
    },
    matchType: "dobles",
    tournamentFormat: "round-robin",
    registrationDeadline: "2025-09-03",
    registeredUsers: [1],
    prizes: [
      {
        category: "Mixtos",
        subcategory: "A",
        firstPlace: [
          { type: "trofeo" },
          { type: "raqueta" },
          { type: "dinero", amount: 150 }
        ]
      }
    ]
  },

  // UPCOMING - Liga Tenis Escalera Dobles
  {
    id: 37,
    title: "Liga Elite Tenis Escalera Piramidal - Dobles Championship",
    sport: "Tenis",
    date: "2025-09-15", // Upcoming
    endDate: "2025-10-25",
    time: "20:30",
    location: "Tennis Academy Elite Center",
    participants: 0,
    maxParticipants: 22,
    registrationFee: 75,
    categories: ["1", "2"],
    duration: "8 semanas",
    matchesPerWeek: 2,
    organizer: "Elite Tennis Academy",
    genderCategories: ["Masculina", "Femenina"],
    subcategories: {
      "Masculina": ["A"],
      "Femenina": ["A"]
    },
    matchType: "dobles",
    isLeague: true,
    registrationDeadline: "2025-09-12",
    leagueFormat: "escalera-piramidal",
    registeredUsers: [1],
    distance: 13.8,
    prizes: [
      {
        category: "Masculina",
        subcategory: "A",
        firstPlace: [
          { type: "trofeo" },
          { type: "dinero", amount: 280 }
        ]
      }
    ]
  },

  // UPCOMING - Liga Pádel Sumatoria Singles
  {
    id: 38,
    title: "Liga Pádel Sumatoria Singles - Open",
    sport: "Pádel",
    date: "2025-09-20", // Upcoming
    endDate: "2025-10-05",
    time: "18:45",
    location: "Centro Pádel Innovación",
    participants: 0,
    maxParticipants: 18,
    registrationFee: 48,
    categories: ["2", "3", "4"],
    duration: "6 semanas",
    matchesPerWeek: 2,
    organizer: "Centro Pádel Innovación",
    genderCategories: ["Mixtos"],
    subcategories: {
      "Mixtos": ["A", "B"]
    },
    matchType: "singles",
    isLeague: true,
    registrationDeadline: "2025-09-17",
    leagueFormat: "sumatoria-juegos",
    registeredUsers: [1],
    distance: 5.9,
    prizes: [
      {
        category: "Mixtos",
        subcategory: "A",
        firstPlace: [
          { type: "trofeo" },
          { type: "pala" },
          { type: "dinero", amount: 120 }
        ]
      }
    ]
  },

  // UPCOMING - Torneo Pickleball Eliminación Singles
  {
    id: 39,
    title: "Torneo Pickleball Eliminación Singles - Masters Cup",
    sport: "Pickleball",
    date: "2025-09-25", // Upcoming
    endDate: "2025-09-28",
    time: "09:30",
    location: "Pickleball Sports Complex",
    participants: 0,
    maxParticipants: 32,
    registrationFee: 38,
    categories: ["2", "3"],
    distance: 10.2,
    organizer: "Pickleball Sports Complex",
    genderCategories: ["Masculina", "Femenina"],
    subcategories: {
      "Masculina": ["A", "B"],
      "Femenina": ["A", "B"]
    },
    matchType: "singles",
    tournamentFormat: "eliminacion-directa",
    registrationDeadline: "2025-09-22",
    registeredUsers: [1],
    prizes: [
      {
        category: "Masculina",
        subcategory: "A",
        firstPlace: [
          { type: "trofeo" },
          { type: "otro", description: "Raqueta Pickleball Premium" },
          { type: "dinero", amount: 200 }
        ]
      }
    ]
  },

  // UPCOMING - Liga Pickleball Escalera Mixtos (for extra variety)
  {
    id: 40,
    title: "Liga Pickleball Escalera - Mixtos Elite",
    sport: "Pickleball",
    date: "2025-09-30", // Upcoming
    endDate: "2025-10-20",
    time: "17:15",
    location: "Club Pickleball Avanzado",
    participants: 0,
    maxParticipants: 24,
    registrationFee: 62,
    categories: ["1", "2"],
    duration: "8 semanas",
    matchesPerWeek: 1,
    organizer: "Club Pickleball Avanzado",
    genderCategories: ["Mixtos"],
    subcategories: {
      "Mixtos": ["A", "B", "C"]
    },
    matchType: "dobles",
    isLeague: true,
    registrationDeadline: "2025-09-27",
    leagueFormat: "escalera-piramidal",
    registeredUsers: [1],
    distance: 8.7,
    prizes: [
      {
        category: "Mixtos",
        subcategory: "A",
        firstPlace: [
          { type: "trofeo" },
          { type: "dinero", amount: 250 }
        ]
      }
    ]
  },

  // ============= ACTIVE EVENTS (6 additional) =============
  {
    id: 41,
    title: "Liga Tenis Round Robin Singles Elite",
    sport: "Tenis", 
    date: "2025-09-03", // Active
    endDate: "2025-09-20",
    time: "17:00",
    location: "Tennis Club Elite",
    participants: 12,
    maxParticipants: 16,
    registrationFee: 45,
    organizer: "Club Deportivo Elite",
    categories: ["1"],
    genderCategories: ["Abierta"],
    subcategories: {
      "Abierta": ["A", "B"]
    },
    matchType: "singles",
    isLeague: true,
    registrationDeadline: "2025-08-31",
    leagueFormat: "round-robin",
    duration: "3 semanas",
    matchesPerWeek: 2,
    registeredUsers: [1],
    distance: 4.2
  },
  {
    id: 42,
    title: "Liga Pickleball Sumatoria Dobles Avanzada",
    sport: "Pickleball",
    date: "2025-09-04", // Active
    endDate: "2025-09-25",
    time: "19:30",
    location: "Pickleball Center Pro",
    participants: 8,
    maxParticipants: 12,
    registrationFee: 38,
    organizer: "Pickleball Academy",
    categories: ["1"],
    genderCategories: ["Abierta"],
    subcategories: {
      "Abierta": ["A", "B"]
    },
    matchType: "dobles",
    isLeague: true,
    registrationDeadline: "2025-09-01",
    leagueFormat: "sumatoria-juegos",
    duration: "3 semanas",
    matchesPerWeek: 2,
    registeredUsers: [1],
    distance: 6.8
  },
  {
    id: 43,
    title: "Torneo Pádel Eliminación Singles Open",
    sport: "Pádel",
    date: "2025-09-06", // Active
    endDate: "2025-09-08",
    time: "10:00",
    location: "Pádel Sports Complex",
    participants: 16,
    maxParticipants: 16,
    registrationFee: 55,
    organizer: "Pádel Pro Events",
    categories: ["1"],
    genderCategories: ["Abierta"],
    subcategories: {
      "Abierta": ["A", "B", "C"]
    },
    matchType: "singles",
    tournamentFormat: "eliminacion-directa",
    registrationDeadline: "2025-09-03",
    registeredUsers: [1],
    distance: 3.5,
    prizes: [
      {
        category: "Abierta",
        subcategory: "A",
        firstPlace: [
          { type: "trofeo" },
          { type: "dinero", amount: 200 }
        ]
      }
    ]
  },
  {
    id: 44,
    title: "Torneo Pickleball Sumatoria Dobles Championship",
    sport: "Pickleball",
    date: "2025-09-07", // Active
    endDate: "2025-09-09",
    time: "08:30",
    location: "Championship Pickleball Arena",
    participants: 10,
    maxParticipants: 12,
    registrationFee: 42,
    organizer: "Pickleball Champions",
    categories: ["1"],
    genderCategories: ["Abierta"],
    subcategories: {
      "Abierta": ["A", "B"]
    },
    matchType: "dobles",
    tournamentFormat: "sumatoria-juegos",
    registrationDeadline: "2025-09-04",
    registeredUsers: [1],
    distance: 5.1,
    prizes: [
      {
        category: "Abierta",
        subcategory: "A",
        firstPlace: [
          { type: "trofeo" },
          { type: "dinero", amount: 180 }
        ]
      }
    ]
  },
  {
    id: 45,
    title: "Liga Pádel Escalera Dobles Pro",
    sport: "Pádel",
    date: "2025-09-08", // Active
    endDate: "2025-09-30",
    time: "18:00",
    location: "Pádel Club Professional",
    participants: 14,
    maxParticipants: 16,
    registrationFee: 48,
    organizer: "Pádel Professional Club",
    categories: ["1"],
    genderCategories: ["Abierta"],
    subcategories: {
      "Abierta": ["A", "B", "C"]
    },
    matchType: "dobles",
    isLeague: true,
    registrationDeadline: "2025-09-05",
    leagueFormat: "escalera-piramidal",
    duration: "3 semanas",
    matchesPerWeek: 3,
    registeredUsers: [1],
    distance: 7.3
  },
  {
    id: 46,
    title: "Torneo Tenis Eliminación Dobles Masters",
    sport: "Tenis",
    date: "2025-09-09", // Active
    endDate: "2025-09-11",
    time: "16:00",
    location: "Tennis Masters Center",
    participants: 8,
    maxParticipants: 8,
    registrationFee: 52,
    organizer: "Tennis Masters Association",
    categories: ["1"],
    genderCategories: ["Abierta"],
    subcategories: {
      "Abierta": ["A", "B"]
    },
    matchType: "dobles",
    tournamentFormat: "eliminacion-directa",
    registrationDeadline: "2025-09-06",
    registeredUsers: [1],
    distance: 9.2,
    prizes: [
      {
        category: "Abierta",
        subcategory: "A",
        firstPlace: [
          { type: "trofeo" },
          { type: "dinero", amount: 220 }
        ]
      }
    ]
  },

  // ============= UPCOMING EVENTS (6 additional) =============
  {
    id: 47,
    title: "Liga Tenis Sumatoria Dobles Elite",
    sport: "Tenis",
    date: "2025-09-16", // Upcoming
    endDate: "2025-10-15",
    time: "17:30",
    location: "Elite Tennis Academy",
    participants: 0,
    maxParticipants: 14,
    registrationFee: 46,
    organizer: "Tennis Elite Academy",
    categories: ["1"],
    genderCategories: ["Abierta"],
    subcategories: {
      "Abierta": ["A", "B", "C"]
    },
    matchType: "dobles",
    isLeague: true,
    registrationDeadline: "2025-09-13",
    leagueFormat: "sumatoria-juegos",
    duration: "4 semanas",
    matchesPerWeek: 2,
    registeredUsers: [1],
    distance: 4.8
  },
  {
    id: 48,
    title: "Liga Pádel Round Robin Singles Championship",
    sport: "Pádel",
    date: "2025-09-18", // Upcoming
    endDate: "2025-10-20",
    time: "19:00",
    location: "Pádel Championship Arena",
    participants: 0,
    maxParticipants: 16,
    registrationFee: 44,
    organizer: "Pádel Championship League",
    categories: ["1"],
    genderCategories: ["Abierta"],
    subcategories: {
      "Abierta": ["A", "B"]
    },
    matchType: "singles",
    isLeague: true,
    registrationDeadline: "2025-09-15",
    leagueFormat: "round-robin",
    duration: "5 semanas",
    matchesPerWeek: 2,
    registeredUsers: [1],
    distance: 6.1
  },
  {
    id: 49,
    title: "Torneo Pádel Round Robin Singles Open",
    sport: "Pádel",
    date: "2025-09-22", // Upcoming
    endDate: "2025-09-24",
    time: "09:00",
    location: "Open Pádel Complex",
    participants: 0,
    maxParticipants: 12,
    registrationFee: 50,
    organizer: "Open Pádel Events",
    categories: ["1"],
    genderCategories: ["Abierta"],
    subcategories: {
      "Abierta": ["A", "B", "C"]
    },
    matchType: "singles",
    tournamentFormat: "round-robin",
    registrationDeadline: "2025-09-19",
    registeredUsers: [1],
    distance: 5.7,
    prizes: [
      {
        category: "Abierta",
        subcategory: "A",
        firstPlace: [
          { type: "trofeo" },
          { type: "dinero", amount: 190 }
        ]
      }
    ]
  },
  {
    id: 50,
    title: "Torneo Tenis Sumatoria Singles Masters",
    sport: "Tenis",
    date: "2025-09-26", // Upcoming
    endDate: "2025-09-28",
    time: "15:00",
    location: "Tennis Singles Masters Court",
    participants: 0,
    maxParticipants: 16,
    registrationFee: 48,
    organizer: "Tennis Singles Masters",
    categories: ["1"],
    genderCategories: ["Abierta"],
    subcategories: {
      "Abierta": ["A", "B"]
    },
    matchType: "singles",
    tournamentFormat: "sumatoria-juegos",
    registrationDeadline: "2025-09-23",
    registeredUsers: [1],
    distance: 8.4,
    prizes: [
      {
        category: "Abierta",
        subcategory: "A",
        firstPlace: [
          { type: "trofeo" },
          { type: "dinero", amount: 210 }
        ]
      }
    ]
  },
  {
    id: 51,
    title: "Liga Pickleball Round Robin Dobles Elite",
    sport: "Pickleball",
    date: "2025-10-02", // Upcoming
    endDate: "2025-11-05",
    time: "18:30",
    location: "Elite Pickleball League Center",
    participants: 0,
    maxParticipants: 12,
    registrationFee: 41,
    organizer: "Elite Pickleball League",
    categories: ["1"],
    genderCategories: ["Abierta"],
    subcategories: {
      "Abierta": ["A", "B", "C"]
    },
    matchType: "dobles",
    isLeague: true,
    registrationDeadline: "2025-09-29",
    leagueFormat: "round-robin",
    duration: "5 semanas",
    matchesPerWeek: 2,
    registeredUsers: [1],
    distance: 7.9
  },
  {
    id: 52,
    title: "Torneo Pickleball Round Robin Dobles Championship",
    sport: "Pickleball",
    date: "2025-10-05", // Upcoming
    endDate: "2025-10-07",
    time: "10:30",
    location: "Championship Pickleball Arena",
    participants: 0,
    maxParticipants: 10,
    registrationFee: 45,
    organizer: "Pickleball Championship Series",
    categories: ["1"],
    genderCategories: ["Abierta"],
    subcategories: {
      "Abierta": ["A", "B"]
    },
    matchType: "dobles",
    tournamentFormat: "round-robin",
    registrationDeadline: "2025-10-02",
    registeredUsers: [1],
    distance: 6.3,
    prizes: [
      {
        category: "Abierta",
        subcategory: "A",
        firstPlace: [
          { type: "trofeo" },
          { type: "dinero", amount: 185 }
        ]
      }
    ]
  }
];

// ============= UTILITY FUNCTIONS =============

export const isUserRegistered = (eventId: number, userId: number = CURRENT_USER_ID): boolean => {
  const event = MASTER_EVENTS.find(e => e.id === eventId);
  return event?.registeredUsers?.includes(userId) || false;
};

export const getAvailableEvents = (userId: number = CURRENT_USER_ID): MasterEvent[] => {
  return MASTER_EVENTS.filter(event => !isUserRegistered(event.id, userId));
};

export const getUserEvents = (userId: number = CURRENT_USER_ID): MasterEvent[] => {
  return MASTER_EVENTS.filter(event => isUserRegistered(event.id, userId));
};

export const getEventsByType = (isLeague: boolean): MasterEvent[] => {
  return MASTER_EVENTS.filter(event => Boolean(event.isLeague) === isLeague);
};

export const getTournaments = (): Tournament[] => {
  return MASTER_EVENTS.filter(event => !event.isLeague) as Tournament[];
};

export const getLeagues = (): League[] => {
  return MASTER_EVENTS.filter(event => event.isLeague) as League[];
};

export const getEventsBySport = (sport: string): MasterEvent[] => {
  return MASTER_EVENTS.filter(event => event.sport.toLowerCase() === sport.toLowerCase());
};

export const getEventById = (id: number): MasterEvent | undefined => {
  return MASTER_EVENTS.find(event => event.id === id);
};

export const registerUserToEvent = (eventId: number, userId: number = CURRENT_USER_ID): boolean => {
  const eventIndex = MASTER_EVENTS.findIndex(e => e.id === eventId);
  if (eventIndex === -1) return false;
  
  const event = MASTER_EVENTS[eventIndex];
  if (!event.registeredUsers) {
    event.registeredUsers = [];
  }
  
  if (!event.registeredUsers.includes(userId)) {
    event.registeredUsers.push(userId);
    return true;
  }
  
  return false;
};

export const unregisterUserFromEvent = (eventId: number, userId: number = CURRENT_USER_ID): boolean => {
  const eventIndex = MASTER_EVENTS.findIndex(e => e.id === eventId);
  if (eventIndex === -1) return false;
  
  const event = MASTER_EVENTS[eventIndex];
  if (!event.registeredUsers) return false;
  
  const userIndex = event.registeredUsers.indexOf(userId);
  if (userIndex > -1) {
    event.registeredUsers.splice(userIndex, 1);
    return true;
  }
  
  return false;
};
